import { Box, Flex, Image, Text, Tooltip, VStack, useClipboard, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { AddressParam } from 'types/api/addressParams';
import type { TokenTransfer } from 'types/api/tokenTransfer';

import { route } from 'nextjs-routes';

import dayjs from 'lib/date/dayjs';
import getCurrencyValue from 'lib/getCurrencyValue';
import useIsSticky from 'lib/hooks/useIsSticky';
import useLazyRenderedList from 'lib/hooks/useLazyRenderedList';
import { useI18n } from 'lib/i18n/context';
import Skeleton from 'ui/shared/chakra/Skeleton';
import LinkInternal from 'ui/shared/links/LinkInternal';
import ScanningNotice, { AnimatedDots } from 'ui/shared/ScanningNotice';
import FinanceStatusTag from 'ui/shared/statusTag/FinanceStatusTag';
import {
  TOKEN_TRANSFERS_ACTION_KEYS,
  TOKEN_TRANSFERS_FIELD_KEYS,
  TOKEN_TRANSFERS_KEYS,
  TOKEN_TRANSFERS_NOTICE_KEYS,
} from 'ui/tokenTransfers/i18n';

const ACCENT_COLOR = '#14DCAC';

type Props = {
  data: Array<TokenTransfer>;
  baseAddress?: string;
  showTxInfo?: boolean;
  showSocketInfo?: boolean;
  socketInfoAlert?: string;
  socketInfoNum?: number;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  top: number;
  desktopTopSlot?: React.ReactNode;
  filterSlot?: React.ReactNode;
  hideValueUsd?: boolean;
};

type ColumnKey = 'txnHash' | 'dateTime' | 'block' | 'fromTo' | 'amount' | 'tokenAddress' | 'method' | 'valueUsd' | 'fee';

const getColumns = (hideValueUsd?: boolean): Array<{ key: ColumnKey; width: string }> => {
  const columns: Array<{ key: ColumnKey; width: string }> = [
    { key: 'txnHash', width: '136px' },
    { key: 'dateTime', width: '80px' },
    { key: 'block', width: '96px' },
    { key: 'fromTo', width: '208px' },
    { key: 'amount', width: '96px' },
    { key: 'tokenAddress', width: '120px' },
    { key: 'method', width: '96px' },
    { key: 'valueUsd', width: '96px' },
    { key: 'fee', width: '96px' },
  ];

  return hideValueUsd ? columns.filter((column) => column.key !== 'valueUsd') : columns;
};

const useExplorerTheme = () => {
  const isLight = useColorModeValue(true, false);

  return {
    isLight,
    borderColor: useColorModeValue('#E9ECEF', '#222222'),
    surfaceColor: useColorModeValue('#FFFFFF', '#111111'),
    subSurfaceColor: useColorModeValue('#FBFCFE', '#171717'),
    primaryText: useColorModeValue('#000000', '#FFFFFF'),
    secondaryText: useColorModeValue('#616161', '#999999'),
    valueText: useColorModeValue('#616161', '#999999'),
    noticeBg: useColorModeValue(
      'linear-gradient(90deg, rgba(20, 220, 172, 1) 0%, rgba(255, 255, 255, 1) 100%)',
      'linear-gradient(90deg, rgba(20, 220, 172, 0.10) 0%, rgba(20, 20, 20, 0.10) 100%)',
    ),
    noticeText: '#FFFFFF',
    noticeAccentColor: useColorModeValue('#FFFFFF', ACCENT_COLOR),
    stickyShadow: useColorModeValue('0 12px 24px rgba(31, 31, 34, 0.08)', '0 12px 24px rgba(0, 0, 0, 0.35)'),
    rowHoverBg: useColorModeValue('#FBFCFE', 'rgba(255,255,255,0.01)'),
    headerInfoSrc: isLight ? '/static/transactions/tx-header-info-light.svg' : '/static/transactions/tx-header-info.svg',
  };
};

const getRowTextSx = (color: string) => ({
  color,
  fontFamily: '\'Oxanium\', sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
});

const getSecondaryTextSx = (color: string) => ({
  color,
  fontFamily: '\'Space Grotesk\', sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
});

const getValueTextSx = (color: string) => ({
  color,
  fontFamily: '\'Oxanium\', sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '24px',
});

const getHeaderLabelSx = (color: string) => ({
  color,
  fontFamily: '\'Space Grotesk\', sans-serif',
  fontWeight: 700,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
});

const getAddressLabel = (address: AddressParam | null) => {
  if (!address) {
    return '-';
  }

  return address.name || address.ens_domain_name || `${ address.hash.slice(0, 8) }...${ address.hash.slice(-8) }`;
};

const CopyButton = ({ text }: { text: string }) => {
  const { primaryText } = useExplorerTheme();
  const { hasCopied, onCopy } = useClipboard(text, 1000);
  const { t } = useI18n();
  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onCopy();
  }, [ onCopy ]);

  return (
    <Tooltip
      label={ hasCopied ? t(TOKEN_TRANSFERS_ACTION_KEYS.copied) : t(TOKEN_TRANSFERS_ACTION_KEYS.copyToClipboard) }
      color="white"
    >
      <Box
        as="button"
        type="button"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        boxSize="16px"
        flexShrink={ 0 }
        onClick={ handleClick }
      >
        <Image src="/static/transactions/tx-copy.svg" alt="" boxSize="16px" color={ primaryText }/>
      </Box>
    </Tooltip>
  );
};

const ExplorerAddressLine = ({
  label,
  address,
  isLoading,
}: {
  label: string;
  address: AddressParam | null;
  isLoading?: boolean;
}) => {
  const { secondaryText, primaryText } = useExplorerTheme();
  const secondaryTextSx = getSecondaryTextSx(secondaryText);
  const rowTextSx = getRowTextSx(primaryText);

  if (isLoading) {
    return (
      <Flex alignItems="center" gap="4px" minW={ 0 }>
        <Text { ...secondaryTextSx } flexShrink={ 0 }>{ label }</Text>
        <Skeleton isLoaded={ false } h="20px" w="132px" borderRadius="4px"/>
        <Skeleton isLoaded={ false } boxSize="16px" borderRadius="4px" flexShrink={ 0 }/>
      </Flex>
    );
  }

  if (!address) {
    return null;
  }

  return (
    <Flex alignItems="center" gap="4px" minW={ 0 }>
      <Text { ...secondaryTextSx } flexShrink={ 0 }>{ label }</Text>
      <Flex alignItems="center" gap="4px" minW={ 0 } flex={ 1 }>
        <LinkInternal
          href={ route({ pathname: '/address/[hash]', query: { hash: address.hash } }) }
          display="inline-flex"
          alignItems="center"
          minW={ 0 }
          flex={ 1 }
          _hover={{ textDecoration: 'none' }}
        >
          <Text { ...rowTextSx } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            { getAddressLabel(address) }
          </Text>
        </LinkInternal>
        <CopyButton text={ address.hash }/>
      </Flex>
    </Flex>
  );
};

const ExplorerPill = ({ children }: { children: React.ReactNode }) => {
  const { borderColor, subSurfaceColor, primaryText } = useExplorerTheme();

  return (
    <Flex
      h="24px"
      px="8px"
      justifyContent="center"
      alignItems="center"
      border="1px solid"
      borderColor={ borderColor }
      borderRadius="4px"
      bg={ subSurfaceColor }
    >
      <Text
        color={ primaryText }
        fontFamily="'Space Grotesk', sans-serif"
        fontWeight={ 500 }
        fontStyle="normal"
        fontSize="12px"
        lineHeight="16px"
        textAlign="center"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        { children }
      </Text>
    </Flex>
  );
};

const ExplorerTokenTransferRow = ({
  item,
  isLoading,
  hideValueUsd,
}: {
  item: TokenTransfer;
  isLoading?: boolean;
  hideValueUsd?: boolean;
}) => {
  const { t } = useI18n();
  const defaultTokenIcon = useColorModeValue('/icons/default_token_white.svg', '/icons/default_token.svg');
  const {
    borderColor,
    primaryText,
    secondaryText,
    valueText,
    rowHoverBg,
  } = useExplorerTheme();
  const rowTextSx = getRowTextSx(primaryText);
  const secondaryTextSx = getSecondaryTextSx(secondaryText);
  const valueTextSx = getValueTextSx(valueText);

  const { valueStr } = item.total && 'value' in item.total && item.total.value !== null ? getCurrencyValue({
    value: item.total.value,
    accuracy: 8,
    decimals: item.total.decimals || '0',
  }) : { valueStr: null };

  const gasFeeStr = item.gas_fee?.value ?
    getCurrencyValue({ value: item.gas_fee.value, decimals: '18', accuracy: 8 }).valueStr :
    null;

  const tokenCell = (() => {
    if (isLoading) {
      return (
        <VStack spacing="4px" alignItems="start">
          <Skeleton isLoaded={ false } h="20px" w="80px" borderRadius="4px"/>
          <Skeleton isLoaded={ false } h="20px" w="96px" borderRadius="4px"/>
        </VStack>
      );
    }

    if (!item.token) {
      return <Text { ...valueTextSx }>-</Text>;
    }

    return (
      <VStack spacing="2px" alignItems="start">
        <Flex alignItems="center" gap="4px" minW={ 0 }>
          <Image
            src={ item.token.icon_url || defaultTokenIcon }
            alt={ item.token.symbol || '' }
            boxSize="16px"
            borderRadius="full"
            flexShrink={ 0 }
          />
          <Text { ...rowTextSx } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            { item.token.symbol || '' }
          </Text>
        </Flex>
        <Flex alignItems="center" gap="4px" minW={ 0 }>
          <Text { ...rowTextSx } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            { `${ item.token.address.slice(0, 6) }...${ item.token.address.slice(-4) }` }
          </Text>
          <CopyButton text={ item.token.address }/>
        </Flex>
      </VStack>
    );
  })();

  const methodCell = (() => {
    if (isLoading) {
      return <Skeleton isLoaded={ false } h="24px" w="80px" borderRadius="4px"/>;
    }

    return item.method ? <ExplorerPill>{ item.method }</ExplorerPill> : null;
  })();

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      gap="16px"
      px="16px"
      py="8px"
      minH="68px"
      borderBottom="1px solid"
      borderColor={ borderColor }
      _hover={{ bg: rowHoverBg }}
    >
      { /* Txn Hash + Status */ }
      <Box width="136px" minW="136px">
        { isLoading ? (
          <VStack spacing="4px" alignItems="start">
            <Flex alignItems="center" gap="4px" minW={ 0 }>
              <Skeleton isLoaded={ false } h="20px" w="108px" borderRadius="4px"/>
              <Skeleton isLoaded={ false } boxSize="16px" borderRadius="4px" flexShrink={ 0 }/>
            </Flex>
            <Skeleton isLoaded={ false } h="20px" w="88px" borderRadius="4px"/>
          </VStack>
        ) : (
          <VStack spacing="4px" alignItems="start">
            { item.transaction_hash ? (
              <Flex alignItems="center" gap="4px" minW={ 0 }>
                <LinkInternal
                  href={ route({ pathname: '/tx/[hash]', query: { hash: item.transaction_hash } }) }
                  display="inline-flex"
                  alignItems="center"
                  minW={ 0 }
                  flex={ 1 }
                  _hover={{ textDecoration: 'none' }}
                >
                  <Text { ...rowTextSx } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    { `${ item.transaction_hash.slice(0, 8) }...${ item.transaction_hash.slice(-6) }` }
                  </Text>
                </LinkInternal>
                <CopyButton text={ item.transaction_hash }/>
              </Flex>
            ) : null }
            { item.status ? (
              <FinanceStatusTag status={ item.status }/>
            ) : null }
          </VStack>
        ) }
      </Box>

      { /* Date Time */ }
      <Flex width="80px" minW="80px" minH="52px" alignItems="center">
        { isLoading ? (
          <VStack spacing="4px" alignItems="start">
            <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>
            <Skeleton isLoaded={ false } h="20px" w="56px" borderRadius="4px"/>
          </VStack>
        ) : (
          <VStack spacing="0" alignItems="start">
            <Text { ...secondaryTextSx } lineHeight="24px">
              { item.timestamp ? dayjs(item.timestamp).format('YYYY/MM/DD') : '-' }
            </Text>
            <Text { ...secondaryTextSx } lineHeight="24px">
              { item.timestamp ? dayjs(item.timestamp).format('HH:mm:ss') : '-' }
            </Text>
          </VStack>
        ) }
      </Flex>

      { /* Block */ }
      <Flex width="96px" minW="96px" minH="52px" alignItems="center">
        { (() => {
          if (isLoading) {
            return <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>;
          }

          if (!item.block_number) {
            return <Text { ...valueTextSx }>-</Text>;
          }

          return (
            <LinkInternal
              href={ route({ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(item.block_number) } }) }
              display="inline-flex"
              alignItems="center"
              minW={ 0 }
              _hover={{ textDecoration: 'none' }}
            >
              <Text { ...rowTextSx } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                { item.block_number }
              </Text>
            </LinkInternal>
          );
        })() }
      </Flex>

      { /* From/To */ }
      <Box width="208px" minW="208px">
        <ExplorerAddressLine label={ t(TOKEN_TRANSFERS_FIELD_KEYS.from) } address={ item.from } isLoading={ isLoading }/>
        <Box mt="4px">
          <ExplorerAddressLine label={ t(TOKEN_TRANSFERS_FIELD_KEYS.to) } address={ item.to } isLoading={ isLoading }/>
        </Box>
      </Box>

      { /* Amount */ }
      <Flex width="96px" minW="96px" minH="52px" alignItems="center">
        { isLoading ? (
          <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>
        ) : (
          <Text { ...valueTextSx }>{ valueStr || '-' }</Text>
        ) }
      </Flex>

      { /* Token/Address */ }
      <Flex width="120px" minW="120px" minH="52px" alignItems="center">
        { tokenCell }
      </Flex>

      { /* Method */ }
      <Flex width="96px" minW="96px" minH="52px" alignItems="center">
        { methodCell }
      </Flex>

      { !hideValueUsd && (
        <Flex width="96px" minW="96px" minH="52px" alignItems="center" justifyContent="flex-end">
          { isLoading ? (
            <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>
          ) : (
            <Text { ...valueTextSx } textAlign="right">{ item.value_usd || 'N/A' }</Text>
          ) }
        </Flex>
      ) }

      { /* Txn Fee (BOT) */ }
      <Flex width="96px" minW="96px" minH="52px" alignItems="center" justifyContent="flex-end" pr={{ base: '16px', lg: 0 }}>
        { isLoading ? (
          <Skeleton isLoaded={ false } h="20px" w="72px" borderRadius="4px"/>
        ) : (
          <Text { ...valueTextSx } textAlign="right">{ gasFeeStr || '-' }</Text>
        ) }
      </Flex>
    </Flex>
  );
};

const TokenTransferExplorerDesktop = ({
  data,
  showSocketInfo,
  socketInfoAlert,
  socketInfoNum,
  isLoading,
  top,
  desktopTopSlot,
  filterSlot,
  hideValueUsd,
}: Props) => {
  const {
    isLight,
    borderColor,
    surfaceColor,
    primaryText,
    secondaryText,
    noticeAccentColor,
    stickyShadow,
  } = useExplorerTheme();
  const { cutRef, renderedItemsNum } = useLazyRenderedList(data, !isLoading);
  const stickySentinelRef = React.useRef<HTMLDivElement>(null);
  const isSticky = useIsSticky(stickySentinelRef, top, true);
  const headerScrollRef = React.useRef<HTMLDivElement>(null);
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);
  const columns = getColumns(hideValueUsd);
  const headerLabelSx = getHeaderLabelSx(primaryText);
  const { t } = useI18n();
  const titleMap: Record<ColumnKey, string> = {
    txnHash: t(TOKEN_TRANSFERS_FIELD_KEYS.txnHash),
    dateTime: t(TOKEN_TRANSFERS_FIELD_KEYS.dateTime),
    block: t('common.block'),
    fromTo: t(TOKEN_TRANSFERS_FIELD_KEYS.fromTo),
    amount: t(TOKEN_TRANSFERS_FIELD_KEYS.amount),
    tokenAddress: t(TOKEN_TRANSFERS_FIELD_KEYS.tokenAddress),
    method: t(TOKEN_TRANSFERS_FIELD_KEYS.method),
    valueUsd: t(TOKEN_TRANSFERS_FIELD_KEYS.valueUsd),
    fee: t(TOKEN_TRANSFERS_FIELD_KEYS.txnFeeBot),
  };

  const handleBodyScroll = React.useCallback(() => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);

  const noticeContent = (() => {
    if (socketInfoAlert) {
      return (
        <Text fontFamily="'Space Grotesk', sans-serif" fontSize="14px" lineHeight="20px" fontWeight={ 400 }>
          { socketInfoAlert }
        </Text>
      );
    }

    if (socketInfoNum) {
      const noticeKey = socketInfoNum === 1 ? TOKEN_TRANSFERS_NOTICE_KEYS.newTransfersOne : TOKEN_TRANSFERS_NOTICE_KEYS.newTransfersOther;

      return (
        <Text fontFamily="'Space Grotesk', sans-serif" fontSize="14px" lineHeight="20px" fontWeight={ 400 }>
          <Text as="span" color={ noticeAccentColor }>
            { t(noticeKey, { count: socketInfoNum.toLocaleString() }) }
          </Text>
        </Text>
      );
    }

    // strip the trailing dots from the localized string; they are rendered as an animation instead
    const scanningText = t(TOKEN_TRANSFERS_NOTICE_KEYS.scanning).replace(/[.…\s]+$/, '');
    return (
      <Text fontFamily="'Space Grotesk', sans-serif" fontSize="14px" lineHeight="20px" fontWeight={ 400 }>
        { scanningText }<AnimatedDots/>
      </Text>
    );
  })();

  return (
    <Box
      bg={ surfaceColor }
      border="1px solid"
      borderColor={ borderColor }
      borderRadius="8px"
      overflow="visible"
    >
      <Box ref={ stickySentinelRef } h={ 0 }/>
      <Box
        position="sticky"
        top={ `${ top }px` }
        zIndex={ 4 }
        transition="box-shadow 0.2s ease, transform 0.2s ease"
      >
        <Box
          bg={ surfaceColor }
          pt="16px"
          pb="8px"
          boxShadow={ isSticky ? stickyShadow : 'none' }
        >
          { /* Top controls: filter (left) / export+pagination (right); stacks on mobile */ }
          <Flex
            px="16px"
            flexDirection={{ base: 'column', lg: 'row' }}
            alignItems={{ base: 'flex-start', lg: 'center' }}
            justifyContent={{ base: 'flex-start', lg: 'space-between' }}
            gap="8px"
            pb="16px"
          >
            <Flex alignItems="center" gap="8px">
              { filterSlot }
            </Flex>
            { desktopTopSlot }
          </Flex>

          <Box>
            { showSocketInfo ? (
              <Box>
                <ScanningNotice>{ noticeContent }</ScanningNotice>
              </Box>
            ) : null }

            { /* Header row — scroll synced with body */ }
            <Box ref={ headerScrollRef } overflowX="hidden">
              <Flex
                px="16px"
                mt={ showSocketInfo ? '4px' : 0 }
                justifyContent="space-between"
                alignItems="center"
                gap="16px"
                pb="8px"
                borderBottom="1px solid"
                borderColor={ borderColor }
              >
                { columns.map((column) => {
                  return (
                    <Flex
                      key={ column.key }
                      width={ column.width }
                      minW={ column.width }
                      h="32px"
                      alignItems="center"
                      justifyContent={ column.key === 'valueUsd' || column.key === 'fee' ? 'flex-end' : 'flex-start' }
                      pr={ column.key === 'fee' ? { base: '16px', lg: 0 } : undefined }
                    >
                      <Text { ...headerLabelSx }>{ titleMap[column.key] }</Text>
                    </Flex>
                  );
                }) }
              </Flex>
            </Box>
          </Box>
        </Box>
      </Box>

      { /* Rows — horizontally scrollable on mobile, syncs header via JS */ }
      { data.length > 0 ? (
        <Box
          ref={ bodyScrollRef }
          overflowX={{ base: 'auto', lg: 'visible' }}
          onScroll={ handleBodyScroll }
        >
          { data.slice(0, renderedItemsNum).map((item, index) => (
            <ExplorerTokenTransferRow
              key={ item.transaction_hash + item.block_hash + item.log_index + (isLoading ? index : '') }
              item={ item }
              isLoading={ isLoading }
              hideValueUsd={ hideValueUsd }
            />
          )) }
          <Box ref={ cutRef } h={ 0 }/>
        </Box>
      ) : (
        <Flex direction="column" alignItems="center" justifyContent="center" py="80px" gap="16px">
          <Image
            src={ isLight ? '/static/transactions/tx-no-data-light.svg' : '/static/transactions/tx-no-data.svg' }
            alt=""
            boxSize="64px"
          />
          <Text
            color={ secondaryText }
            fontFamily="'Space Grotesk', sans-serif"
            fontSize="14px"
            lineHeight="20px"
            fontWeight={ 400 }
          >
            { t(TOKEN_TRANSFERS_KEYS.empty.noData) }
          </Text>
        </Flex>
      ) }
    </Box>
  );
};

export default React.memo(TokenTransferExplorerDesktop);
